import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, Loader2, X } from "lucide-react";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useMsal } from "@azure/msal-react";
import { cn } from "@/utils/cn";
import { usePermissions } from "@/hooks/usePermissions";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { grcKeys } from "@/services/grc";
import {
  useBusinessAdvisorLookup,
  softHoldKeys,
  useAddSoftHoldComment,
  useCreateSoftHold,
  useEditSoftHold,
  useEditSoftHoldComment,
  useResolveSoftHold,
  useSoftHoldById,
  useSoftHoldHistoryById,
  useSoftHoldCategories,
  useSoftHoldMeetingTypes,
  useSoftHoldSubCategories,
  useSummitMeetingLookup,
  checkMeetingLinkedToSoftHold
} from "@/services/softHolds";
import type { CreateSoftHoldRequest, EditSoftHoldRequest } from "@/types/softHolds";

const createSoftHoldSchema = z
  .object({
    meetingName: z.string().min(1, "Meeting name is required"),
    meetingId: z
      .string()
      .trim()
      .refine((v) => v === "" || /^\d+$/.test(v), "Meeting ID must contain only numbers"),
    meetingType: z.string().min(1, "Meeting type is required"),
    category: z.string().optional(),
    subCategory: z.string().optional(),
    businessAdvisor: z.string().min(1, "Business advisor is required"),
    businessAdvisorEmail: z.string(),
    summitMeetingStart: z.string().optional(),
    summitMeetingEnd: z.string().optional(),
    notes: z.string().optional()
  })
  .refine(
    (value) => {
      if (!value.summitMeetingStart || !value.summitMeetingEnd) return true;
      return value.summitMeetingEnd >= value.summitMeetingStart;
    },
    {
      message: "Summit meeting end date must be on or after summit meeting start date",
      path: ["summitMeetingEnd"]
    }
  );

type CreateSoftHoldValues = z.infer<typeof createSoftHoldSchema>;

export type BlockCountDay = {
  day: string;
  blockCount: number;
  isDeleted: boolean;
};

export type RoomBlockOption = {
  id: number;
  startDate: string;
  endDate: string;
  meetingStartDate: string;
  meetingEndDate: string;
  estimatedAttendance: number;
  isSelected: boolean;
  isItPrimaryDate: boolean;
  blockCountDayWise: BlockCountDay[];
};

type RoomBlockOptionError = {
  startDate?: string;
  endDate?: string;
  meetingEndDate?: string;
};

type SoftHoldDialogTab = "details" | "history";

function getSoftHoldInitials(name: string): string {
  if (!name.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

interface CreateSoftHoldDialogProps {
  month: number;
  year: number;
  softHoldId?: number;
  isEditMode?: boolean;
  /** Pre-seed the room-block options from an Atlas recommendation. */
  initialRoomBlockOptions?: RoomBlockOption[];
  onClose: () => void;
  onCreated?: () => void;
}

export function CreateSoftHoldDialog({
  month,
  year,
  softHoldId,
  isEditMode = false,
  initialRoomBlockOptions,
  onClose,
  onCreated
}: CreateSoftHoldDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEditSoftHold = hasPermission("CanEditSoftHold");
  const createSoftHold = useCreateSoftHold();
  const editSoftHold = useEditSoftHold();
  const resolveSoftHold = useResolveSoftHold();
  const addSoftHoldComment = useAddSoftHoldComment();
  const editSoftHoldComment = useEditSoftHoldComment();
  const { accounts } = useMsal();
  const currentUser = useCurrentUser();
  const userEmail = accounts[0]?.username ?? "";
  const hasExistingSoftHold = Boolean(softHoldId && softHoldId > 0);
  const [isEditingMode, setIsEditingMode] = useState(hasExistingSoftHold && isEditMode);
  const isDetailsMode = hasExistingSoftHold && !isEditingMode;
  const [activeTab, setActiveTab] = useState<SoftHoldDialogTab>("details");
  const [meetingLookupInput, setMeetingLookupInput] = useState("");
  const [showMeetingLookup, setShowMeetingLookup] = useState(false);
  const [meetingLookupError, setMeetingLookupError] = useState<string | null>(null);
  const [isMeetingLinkedToSummit, setIsMeetingLinkedToSummit] = useState(false);
  const [businessAdvisorLookupInput, setBusinessAdvisorLookupInput] = useState("");
  const [showBusinessAdvisorLookup, setShowBusinessAdvisorLookup] = useState(false);
  const [showResolveDialog, setShowResolveDialog] = useState(false);
  const [resolveStatus, setResolveStatus] = useState("");
  const [resolvePreferredDateId, setResolvePreferredDateId] = useState<number | "">("");
  const [resolveNotes, setResolveNotes] = useState("");
  const [resolveStatusError, setResolveStatusError] = useState<string | null>(null);
  const [resolveDateError, setResolveDateError] = useState<string | null>(null);
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const _initialOptions: RoomBlockOption[] = initialRoomBlockOptions?.length
    ? initialRoomBlockOptions
    : [{
        id: 1,
        startDate: "",
        endDate: "",
        meetingStartDate: "",
        meetingEndDate: "",
        estimatedAttendance: 0,
        isSelected: true,
        isItPrimaryDate: false,
        blockCountDayWise: []
      }];
  const [roomBlockOptions, setRoomBlockOptions] = useState<RoomBlockOption[]>(_initialOptions);
  const [nextRoomBlockOptionId, setNextRoomBlockOptionId] = useState(_initialOptions.length + 1);
  const [expandedOptionId, setExpandedOptionId] = useState<number | null>(
    _initialOptions.at(-1)?.id ?? 1
  );
  const [primaryOptionId, setPrimaryOptionId] = useState<number>(_initialOptions[0]?.id ?? 1);
  const [roomBlockOptionErrors, setRoomBlockOptionErrors] = useState<Record<number, RoomBlockOptionError>>({});
  const isLinkedMode = isDetailsMode || isMeetingLinkedToSummit;
  const { data: softHoldDetails, isPending: isSoftHoldDetailsPending } = useSoftHoldById(
    softHoldId ?? 0,
    hasExistingSoftHold
  );
  const { data: softHoldHistory = [], isPending: isSoftHoldHistoryPending } = useSoftHoldHistoryById(
    softHoldId ?? 0,
    hasExistingSoftHold && activeTab === "history"
  );
  const { data: summitMeetingResults = [], isPending: isSummitLookupPending } = useSummitMeetingLookup(
    meetingLookupInput,
    !isDetailsMode && showMeetingLookup && /^\d+$/.test(meetingLookupInput.trim())
  );
  const { data: businessAdvisorResults = [], isPending: isBusinessAdvisorLookupPending } = useBusinessAdvisorLookup(
    businessAdvisorLookupInput,
    !isDetailsMode && !isLinkedMode && showBusinessAdvisorLookup
  );

  const resolveReasons = useMemo(() => {
    if (!softHoldDetails?.meetingId) return ["Did Not Materialize"];
    return ["Confirmed Event"];
  }, [softHoldDetails?.meetingId]);

  useEffect(() => {
    setIsEditingMode(hasExistingSoftHold && isEditMode);
  }, [hasExistingSoftHold, isEditMode]);

  useEffect(() => {
    if (!hasExistingSoftHold) {
      setActiveTab("details");
      return;
    }

    if (isEditingMode) {
      setActiveTab("details");
    }
  }, [hasExistingSoftHold, isEditingMode]);

  useEffect(() => {
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, []);

  function handlePanelKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "Escape") {
      onClose();
    }
  }

  const form = useForm<CreateSoftHoldValues>({
    resolver: zodResolver(createSoftHoldSchema),
    defaultValues: {
      meetingName: "",
      meetingId: "",
      meetingType: "",
      category: "",
      subCategory: "",
      businessAdvisor: "",
      businessAdvisorEmail: "",
      summitMeetingStart: "",
      summitMeetingEnd: "",
      notes: ""
    }
  });

  const { data: meetingTypes = [], isPending: isMeetingTypesPending } = useSoftHoldMeetingTypes();
  const selectedMeetingTypeName = form.watch("meetingType");
  const selectedCategoryName = form.watch("category");

  const selectedMeetingTypeId = useMemo(() => {
    const selectedMeetingType = meetingTypes.find((item) => item.name === selectedMeetingTypeName);
    return selectedMeetingType?.id ?? null;
  }, [meetingTypes, selectedMeetingTypeName]);

  const { data: categories = [] } = useSoftHoldCategories(selectedMeetingTypeId);

  const selectedCategoryId = useMemo(() => {
    const selectedCategory = categories.find((item) => item.category === selectedCategoryName);
    return selectedCategory?.id ?? null;
  }, [categories, selectedCategoryName]);

  const { data: subCategories = [] } = useSoftHoldSubCategories(selectedCategoryId);

  useEffect(() => {
    if (!hasExistingSoftHold || !softHoldDetails) return;

    form.reset({
      meetingName: softHoldDetails.meetingName ?? "",
      meetingId: softHoldDetails.meetingId ?? "",
      meetingType: softHoldDetails.meetingType ?? "",
      category: softHoldDetails.meetingCategory ?? "",
      subCategory: softHoldDetails.meetingSubCategory ?? "",
      businessAdvisor: softHoldDetails.businessAdvisor ?? "",
      businessAdvisorEmail: softHoldDetails.businessAdvisorEmail ?? "",
      summitMeetingStart: toDateInputValue(softHoldDetails.summitMeetingStart),
      summitMeetingEnd: toDateInputValue(softHoldDetails.summitMeetingEnd),
      notes: softHoldDetails.notes ?? ""
    });
    const mappedOptions =
      softHoldDetails.preferredDates.length > 0
        ? softHoldDetails.preferredDates.map((dateOption, index) => ({
            id: index + 1,
            startDate: toDateInputValue(dateOption.startDate),
            endDate: toDateInputValue(dateOption.endDate),
            meetingStartDate: toDateInputValue(dateOption.meetingStartDate),
            meetingEndDate: toDateInputValue(dateOption.meetingEndDate),
            estimatedAttendance: Math.max(0, dateOption.estimatedAttendance ?? 0),
            isSelected: Boolean(dateOption.isSelected),
            isItPrimaryDate: Boolean(dateOption.isItPrimaryDate),
            blockCountDayWise: (dateOption.blockCountDayWise ?? []).map((day) => ({
              day: toDateInputValue(day.day),
              blockCount: Math.max(0, Number(day.blockCount) || 0),
              isDeleted: Boolean(day.isDeleted)
            }))
          }))
        : [
            {
              id: 1,
              startDate: "",
              endDate: "",
              meetingStartDate: "",
              meetingEndDate: "",
              estimatedAttendance: 0,
              isSelected: true,
              isItPrimaryDate: false,
              blockCountDayWise: []
            }
          ];

    setRoomBlockOptions(mappedOptions);
    setNextRoomBlockOptionId(mappedOptions.length + 1);
    setExpandedOptionId(mappedOptions[mappedOptions.length - 1]?.id ?? 1);
    setPrimaryOptionId(mappedOptions[0]?.id ?? 1);
    setRoomBlockOptionErrors({});
    setMeetingLookupInput(softHoldDetails.meetingId ?? "");
    setIsMeetingLinkedToSummit(Boolean(softHoldDetails.meetingId));
    setBusinessAdvisorLookupInput(softHoldDetails.businessAdvisorEmail ?? softHoldDetails.businessAdvisor ?? "");
  }, [form, hasExistingSoftHold, softHoldDetails]);

  useEffect(() => {
    if (hasExistingSoftHold) return;
    const currentMeetingId = form.watch("meetingId")?.trim() ?? "";
    if (!currentMeetingId) {
      setMeetingLookupInput("");
      setIsMeetingLinkedToSummit(false);
      setMeetingLookupError(null);
    }
  }, [form, hasExistingSoftHold]);

  async function handleMeetingLookupSelection(selectedMeetingId: string) {
    const selected = summitMeetingResults.find(
      (item) => (item.summitMeetingId ?? "") === selectedMeetingId
    );

    if (!selected) return;

    // Block on genuine API errors unrelated to an existing soft hold
    if (selected.errorMessage && !selected.isSoftholdExists) {
      setMeetingLookupError(selected.errorMessage);
      return;
    }

    // Mirror Blazor's IsMeetingLinkedToActiveSoftHold check.
    // Pass softHoldId = 0 when creating so the API includes all active soft holds;
    // pass the current record id when editing so the API excludes it.
    // Skip the call entirely when summitMeetingId is absent — nothing to check.
    let isLinkedToActiveSoftHold = false;
    if (selected.summitMeetingId) {
      try {
        isLinkedToActiveSoftHold = await checkMeetingLinkedToSoftHold(
          selected.summitMeetingId,
          softHoldId ?? 0
        );
      } catch {
        // If the API check fails, fall back to the isSoftholdExists flag
        // already returned by the lookup query.
      }
    }

    // If meeting is already linked to an active soft hold, show inline error and block.
    // The form is not populated so the user cannot save until they clear or pick a different meeting.
    if (isLinkedToActiveSoftHold || selected.isSoftholdExists) {
      const errorMessage =
        selected.errorMessage?.trim() ||
        "A soft hold already exists for this meeting. Please clear the field and select a different meeting.";
      setMeetingLookupError(errorMessage);
      setShowMeetingLookup(false);
      return;
    }

    form.setValue("meetingId", selected.summitMeetingId ?? "");
    form.setValue("meetingName", selected.meetingName ?? "");
    form.setValue("meetingType", selected.meetingType ?? "");
    form.setValue("category", selected.category ?? "");
    form.setValue("subCategory", selected.subCategory ?? "");
    form.setValue("businessAdvisor", selected.businessAdvisorName ?? "");
    form.setValue("businessAdvisorEmail", selected.businessAdvisorEmail ?? "");
    form.setValue("summitMeetingStart", toDateInputValue(selected.meetingStartDate));
    form.setValue("summitMeetingEnd", toDateInputValue(selected.meetingEndDate));

    setMeetingLookupInput(selected.summitMeetingId ?? "");
    setBusinessAdvisorLookupInput(selected.businessAdvisorEmail ?? "");
    setMeetingLookupError(null);
    setIsMeetingLinkedToSummit(true);
    setShowMeetingLookup(false);
  }

  function handleBusinessAdvisorSelection(advisorEmail: string) {
    const selectedAdvisor = businessAdvisorResults.find((item) => item.email === advisorEmail);
    if (!selectedAdvisor) return;

    const displayName = selectedAdvisor.displayName || selectedAdvisor.fullName;
    form.setValue("businessAdvisor", displayName ?? "");
    form.setValue("businessAdvisorEmail", selectedAdvisor.email ?? "");
    setBusinessAdvisorLookupInput(selectedAdvisor.email ?? "");
    setShowBusinessAdvisorLookup(false);
  }

  function handleUnlinkSummitMeeting() {
    if (isDetailsMode) return;

    form.setValue("meetingId", "");
    setMeetingLookupInput("");
    setShowMeetingLookup(false);
    setMeetingLookupError(null);
    setIsMeetingLinkedToSummit(false);
  }

  function openResolveDialog() {
    setResolveStatus("");
    setResolvePreferredDateId("");
    setResolveNotes("");
    setResolveStatusError(null);
    setResolveDateError(null);
    setShowResolveDialog(true);
  }

  async function handleResolveSave() {
    if (!softHoldDetails) return;

    let hasError = false;

    if (!resolveStatus) {
      setResolveStatusError("Please select reason to resolve soft hold.");
      hasError = true;
    } else {
      setResolveStatusError(null);
    }

    if (resolveStatus === "Confirmed Event" && !resolvePreferredDateId) {
      setResolveDateError("Please select confirmed date.");
      hasError = true;
    } else {
      setResolveDateError(null);
    }

    if (hasError) return;

    try {
      await resolveSoftHold.mutateAsync({
        softHoldId: softHoldDetails.id,
        status: resolveStatus,
        meetingId: softHoldDetails.meetingId || undefined,
        resolvedPreferredDateId:
          resolveStatus === "Confirmed Event" && resolvePreferredDateId
            ? Number(resolvePreferredDateId)
            : undefined,
        notes: resolveNotes.trim() || undefined
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: grcKeys.byMonth(month, year) }),
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byMonth(month, year) }),
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(softHoldDetails.id) })
      ]);

      toast.success("Soft hold resolved successfully.");
      setShowResolveDialog(false);
    } catch {
      toast.error("Unable to resolve soft hold.");
    }
  }

  function updateRoomBlockOption(
    optionId: number,
    updater: (previousOption: RoomBlockOption) => RoomBlockOption
  ) {
    setRoomBlockOptions((previousOptions) =>
      previousOptions.map((option) => (option.id === optionId ? updater(option) : option))
    );
  }

  function rebuildBlockDays(startDate: string, endDate: string, previousRows: BlockCountDay[]) {
    if (!startDate || !endDate || endDate < startDate) return [];
    const previousMap = new Map(previousRows.map((row) => [row.day, row]));
    const generatedDays = buildDayRange(startDate, endDate);

    return generatedDays.map((day) => {
      const existing = previousMap.get(day);
      return {
        day,
        blockCount: existing ? Math.max(0, existing.blockCount) : 0,
        isDeleted: false
      };
    });
  }

  function addRoomBlockOption() {
    if (isDetailsMode) return;
    const createdId = nextRoomBlockOptionId;
    setRoomBlockOptions((previousOptions) => [
      ...previousOptions,
      {
        id: createdId,
        startDate: "",
        endDate: "",
        meetingStartDate: "",
        meetingEndDate: "",
        estimatedAttendance: 0,
        isSelected: true,
        isItPrimaryDate: false,
        blockCountDayWise: []
      }
    ]);
    setNextRoomBlockOptionId((current) => current + 1);
    setExpandedOptionId(createdId);
  }

  function removeRoomBlockOption(optionId: number) {
    if (isDetailsMode) return;
    setRoomBlockOptions((previousOptions) => {
      if (previousOptions.length <= 1) return previousOptions;

      const filtered = previousOptions.filter((option) => option.id !== optionId);
      if (expandedOptionId === optionId) {
        setExpandedOptionId(filtered[filtered.length - 1]?.id ?? null);
      }
      if (primaryOptionId === optionId) {
        setPrimaryOptionId(filtered[0]?.id ?? 1);
      }
      return filtered;
    });
    setRoomBlockOptionErrors((previousErrors) => {
      const nextErrors = { ...previousErrors };
      delete nextErrors[optionId];
      return nextErrors;
    });
  }

  async function handleAddComment() {
    if (!newComment.trim() || !softHoldDetails) return;
    try {
      await addSoftHoldComment.mutateAsync({
        comment: newComment.trim(),
        draftMeetingId: softHoldDetails.id,
        isActive: true,
        userId: currentUser?.id ?? 0,
        userName: currentUser?.userName || userEmail,
        createdBy: userEmail,
        updatedBy: userEmail
      });
      await queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(softHoldDetails.id) });
      setNewComment("");
    } catch {
      toast.error("Unable to add comment.");
    }
  }

  async function handleSaveEditComment(commentId: number) {
    if (!editingCommentText.trim() || !softHoldDetails) return;
    const original = softHoldDetails.softholdComments.find((c) => c.id === commentId);
    if (!original) return;
    try {
      await editSoftHoldComment.mutateAsync({
        id: commentId,
        comment: editingCommentText.trim(),
        draftMeetingId: softHoldDetails.id,
        isActive: true,
        createdBy: original.createdBy,
        createdDate: original.createdDate,
        userId: currentUser?.id ?? 0,
        userName: currentUser?.userName || userEmail
      });
      await queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(softHoldDetails.id) });
      setEditingCommentId(null);
      setEditingCommentText("");
    } catch {
      toast.error("Unable to update comment.");
    }
  }

  async function onSubmit(values: CreateSoftHoldValues) {
    if (isDetailsMode) return;

    const validationErrors: Record<number, RoomBlockOptionError> = {};
    roomBlockOptions.forEach((option) => {
      const optionError: RoomBlockOptionError = {};
      if (!option.startDate) {
        optionError.startDate = "Room block start date is required";
      }
      if (!option.endDate) {
        optionError.endDate = "Room block end date is required";
      }
      if (option.startDate && option.endDate && option.endDate < option.startDate) {
        optionError.endDate = "Room block end date must be on or after start date";
      }
      if (
        option.meetingStartDate &&
        option.meetingEndDate &&
        option.meetingEndDate < option.meetingStartDate
      ) {
        optionError.meetingEndDate = "Meeting end date must be on or after meeting start date";
      }

      if (Object.keys(optionError).length > 0) {
        validationErrors[option.id] = optionError;
      }
    });

    if (roomBlockOptions.length < 1) {
      toast.error("At least one room block option is required.");
      return;
    }

    if (Object.keys(validationErrors).length > 0) {
      setRoomBlockOptionErrors(validationErrors);
      toast.error("Please fix errors in Dates and Room Blocks options.");
      return;
    }

    setRoomBlockOptionErrors({});

    const basePayload: CreateSoftHoldRequest = {
      meetingName: values.meetingName.trim(),
      meetingId: values.meetingId.trim() || undefined,
      meetingType: values.meetingType,
      organizerName: "",
      email: values.businessAdvisorEmail.trim() || undefined,
      status: softHoldDetails?.status || "Soft Hold",
      isDeleted: false,
      notes: values.notes?.trim() || undefined,
      businessAdvisor: values.businessAdvisor.trim(),
      businessAdvisorEmail: values.businessAdvisorEmail.trim(),
      category: values.category || undefined,
      subCategory: values.subCategory || undefined,
      summitMeetingStart: values.summitMeetingStart || undefined,
      summitMeetingEnd: values.summitMeetingEnd || undefined,
      softHoldPreferredDates: roomBlockOptions.map((option, index) => ({
        startDate: option.startDate,
        endDate: option.endDate,
        meetingStartDate: option.meetingStartDate || option.startDate,
        meetingEndDate: option.meetingEndDate || option.endDate,
        status: "",
        isDeleted: false,
        isSelected: option.isSelected,
        isItPrimaryDate: option.isItPrimaryDate,
        estimatedAttendance: Math.max(0, option.estimatedAttendance),
        blockCountDayWise:
          option.blockCountDayWise.length > 0
            ? option.blockCountDayWise.map((row) => ({
                day: row.day,
                blockCount: Math.max(0, row.blockCount),
                isDeleted: row.isDeleted
              }))
            : buildBlockCountDays(option.startDate, option.endDate)
      }))
    };

    try {
      if (isEditingMode && softHoldDetails) {
        const editPayload: EditSoftHoldRequest = {
          ...basePayload,
          id: softHoldDetails.id,
          softholdComments: []
        };

        await editSoftHold.mutateAsync(editPayload);

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: grcKeys.byMonth(month, year) }),
          queryClient.invalidateQueries({ queryKey: softHoldKeys.byMonth(month, year) }),
          queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(softHoldDetails.id) }),
          queryClient.invalidateQueries({ queryKey: softHoldKeys.historyById(softHoldDetails.id) })
        ]);

        toast.success("Soft hold updated successfully.");
      } else {
        const createdSoftHoldId = await createSoftHold.mutateAsync(basePayload);

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: grcKeys.byMonth(month, year) }),
          queryClient.invalidateQueries({ queryKey: softHoldKeys.byMonth(month, year) }),
          createdSoftHoldId > 0
            ? queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(createdSoftHoldId) })
            : Promise.resolve()
        ]);

        // Save notes as the first comment — done after cache invalidation so the
        // soft hold record is fully committed before the comment is posted.
        const noteText = values.notes?.trim();
        if (noteText && createdSoftHoldId > 0) {
          try {
            await addSoftHoldComment.mutateAsync({
              comment: noteText,
              draftMeetingId: createdSoftHoldId,
              isActive: true,
              userId: currentUser?.id ?? 0,
              userName: currentUser?.userName || userEmail,
              createdBy: userEmail,
              updatedBy: userEmail
            });
            await queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(createdSoftHoldId) });
          } catch {
            toast.error("Soft hold created, but the note could not be saved as a comment.");
          }
        }

        toast.success("Soft hold created successfully.");
      }

      onCreated?.();
      onClose();
    } catch {
      toast.error(
        isEditingMode
          ? "Unable to update soft hold. Please verify required fields and try again."
          : "Unable to create soft hold. Please verify required fields and try again."
      );
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-[70] bg-black/30" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-soft-hold-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-[80] ml-auto flex h-full w-full max-w-3xl flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handlePanelKeyDown}
      >
        <div className="flex items-center justify-between bg-black px-4 py-3">
          <div className="flex items-center gap-3">
            <h2 id="create-soft-hold-title" className="text-base font-semibold text-white">
              {isDetailsMode ? (softHoldDetails?.meetingName || "Soft Hold Details") : isEditingMode ? "Edit Soft Hold" : "Add Soft Hold"}
            </h2>
            {softHoldDetails?.isResolved && (
              <span className="rounded-none bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800">
                Resolved
              </span>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close create soft hold dialog" className="text-white hover:text-slate-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        {hasExistingSoftHold && (
          <div
            role="tablist"
            aria-label="Soft hold sections"
            className="flex items-end gap-0 bg-white px-4 pt-4"
          >
            <button
              role="tab"
              type="button"
              id="tab-details"
              aria-controls="tabpanel-details"
              aria-selected={activeTab === "details"}
              onClick={() => setActiveTab("details")}
              className={cn(
                "relative -mb-px px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none",
                activeTab === "details"
                  ? "border border-b-white border-slate-200 bg-black text-white"
                  : "border border-transparent text-slate-600 hover:text-slate-900"
              )}
            style={{ width: "50%" }}>
              Details
            </button>
            <button
              role="tab"
              type="button"
              id="tab-history"
              aria-controls="tabpanel-history"
              aria-selected={activeTab === "history"}
              onClick={() => {
                if (!isEditingMode) setActiveTab("history");
              }}
              disabled={isEditingMode}
              title={isEditingMode ? "History is unavailable while editing." : undefined}
              className={cn(
                "relative -mb-px px-5 py-2.5 text-sm font-semibold transition-colors focus:outline-none",
                activeTab === "history"
                  ? "border border-b-white border-slate-200 bg-black text-white"
                  : "border border-transparent text-slate-600 hover:text-slate-900",
                isEditingMode && "cursor-not-allowed opacity-40"
              )}
            style={{ width: "50%" }}>
              History
            </button>
          </div>
        )}

        {hasExistingSoftHold && activeTab === "details" && isSoftHoldDetailsPending ? (
          <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Loading soft hold details...
          </div>
        ) : hasExistingSoftHold && activeTab === "history" ? (
          <div
            id="tabpanel-history"
            role="tabpanel"
            aria-labelledby="tab-history"
            className="flex-1 overflow-y-auto px-4 pb-4"
          >
            {isSoftHoldHistoryPending ? (
              <div className="flex items-center justify-center py-10 text-sm text-slate-500">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading soft hold history...
              </div>
            ) : softHoldHistory.length === 0 ? (
              <div className="py-6 text-sm text-slate-500">No soft holds history.</div>
            ) : (
              <ul className="space-y-0 border border-slate-200 px-10 py-2 text-slate-700">
                {softHoldHistory.map((item, index) => (
                  <li
                    key={`${item.softHoldId}-${index}`}
                    className={cn(
                      "grid grid-cols-[1fr_auto] gap-4 px-3 py-3 text-sm",
                      index % 2 === 0 ? "bg-white" : "bg-slate-50"
                    )}
                  >
                    <span className="text-slate-800">{item.displayMessage}</span>
                    <span className="whitespace-nowrap text-xs text-slate-500">{item.createdDate}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) 
        : 
        (
          <div className="flex flex-1 flex-col overflow-auto px-4 pb-4 border-b border-slate-200">
            <div className="border border-slate-200">
            <form
              id="create-soft-hold-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="flex-1 space-y-5 overflow-y-auto px-4 py-4"
            >
          <section className="space-y-3">
            <h3 className="text-sm font-semibold text-slate-900">General Information</h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              
                <Field label="Summit Meeting ID" 
                htmlFor="softhold-meeting-id" 
                error={form.formState.errors.meetingId?.message} 
                disabled={isLinkedMode}>
                
                <div className="relative">
                  <input
                    id="softhold-meeting-id"
                    type="text"
                    value={meetingLookupInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      setMeetingLookupInput(value);
                      form.setValue("meetingId", value);
                      setMeetingLookupError(null);
                      setShowMeetingLookup(Boolean(value.trim()));
                      if (!value.trim()) {
                        setIsMeetingLinkedToSummit(false);
                      }
                    }}
                    onFocus={() => {
                      if (!isDetailsMode && meetingLookupInput.trim()) {
                        setShowMeetingLookup(true);
                      }
                    }}
                    readOnly={isDetailsMode}
                    className={cn(inputCls(!!form.formState.errors.meetingId, true), !isDetailsMode && isMeetingLinkedToSummit && "pr-7")}
                    placeholder="Search Summit Meeting ID"
                  />

                  {showMeetingLookup && !isDetailsMode && (
                    <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-none border border-slate-200 bg-white shadow-lg">
                      {!/^\d+$/.test(meetingLookupInput.trim()) ? (
                        <p className="px-3 py-2 text-xs text-slate-500">
                          Enter numeric Meeting ID to search Summit.
                        </p>
                      ) : isSummitLookupPending ? (
                        <p className="px-3 py-2 text-xs text-slate-500">Searching Summit...</p>
                      ) : summitMeetingResults.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-500">No Summit meeting found.</p>
                      ) : (
                        summitMeetingResults.map((result) => (
                          <button
                            key={`${result.summitMeetingId}-${result.meetingName}`}
                            type="button"
                            onClick={() => handleMeetingLookupSelection(result.summitMeetingId ?? "")}
                            className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                          >
                            <span className="font-semibold">{result.summitMeetingId}</span>
                            <span className="ml-2">{result.meetingName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  {!isDetailsMode && isMeetingLinkedToSummit && (
                    <button
                      type="button"
                      onClick={handleUnlinkSummitMeeting}
                      aria-label="Unlink Summit Meeting"
                      className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center hover:opacity-75"
                    >
                      <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11.3922 8.7596L12.6668 10.0342V8.7596H11.3922ZM13.4585 5.59293H10.2918V7.0971H13.4585C14.8122 7.0971 15.9127 8.19751 15.9127 9.55126C15.9127 10.5567 15.3031 11.4275 14.4322 11.7996L15.5406 12.9079C16.6647 12.2113 17.4168 10.9683 17.4168 9.55126C17.4168 7.36626 15.6435 5.59293 13.4585 5.59293ZM1.5835 3.43168L4.04558 5.89376C2.60475 6.4796 1.5835 7.89668 1.5835 9.55126C1.5835 11.7363 3.35683 13.5096 5.54183 13.5096H8.7085V12.0054H5.54183C4.18808 12.0054 3.08766 10.905 3.08766 9.55126C3.08766 8.29251 4.04558 7.25543 5.27266 7.12085L6.91141 8.7596H6.3335V10.3429H8.49474L10.2918 12.14V13.5096H11.6614L14.836 16.6842L15.9522 15.5679L2.69975 2.31543L1.5835 3.43168Z" fill="#DA291C" />
                      </svg>
                    </button>
                  )}
                </div>

                {meetingLookupError ? (
                  <p className="mt-1 text-xs text-red-600">{meetingLookupError}</p>
                ) : (
                  form.formState.errors.meetingId?.message && (
                    <p className="mt-1 text-xs text-red-600">{form.formState.errors.meetingId?.message}</p>
                  )
                )}
              
</Field>
              <Field label="Business Advisor" htmlFor="softhold-business-advisor" required error={form.formState.errors.businessAdvisor?.message} disabled={isLinkedMode}>
                <div className="relative">
                  <input
                    id="softhold-business-advisor"
                    type="text"
                    value={businessAdvisorLookupInput}
                    onChange={(event) => {
                      const value = event.target.value;
                      setBusinessAdvisorLookupInput(value);
                      setShowBusinessAdvisorLookup(Boolean(value.trim()));
                      form.setValue("businessAdvisor", "");
                      form.setValue("businessAdvisorEmail", value.trim());
                    }}
                    onFocus={() => {
                      if (!isLinkedMode && businessAdvisorLookupInput.trim()) {
                        setShowBusinessAdvisorLookup(true);
                      }
                    }}
                    readOnly={isLinkedMode}
                    className={inputCls(!!form.formState.errors.businessAdvisor, true)}
                    placeholder="Search by advisor email"
                  />

                  {showBusinessAdvisorLookup && !isLinkedMode && (
                    <div className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-none border border-slate-200 bg-white shadow-lg">
                      {isBusinessAdvisorLookupPending ? (
                        <p className="px-3 py-2 text-xs text-slate-500">Searching business advisors...</p>
                      ) : businessAdvisorResults.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-500">No business advisors found.</p>
                      ) : (
                        businessAdvisorResults.map((advisor) => (
                          <button
                            key={`${advisor.id}-${advisor.email}`}
                            type="button"
                            onClick={() => handleBusinessAdvisorSelection(advisor.email)}
                            className="block w-full px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                          >
                            <span className="font-semibold">{advisor.email}</span>
                            <span className="ml-2">{advisor.displayName || advisor.fullName}</span>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              </Field>

              <Field label="Summit Meeting Start" htmlFor="softhold-meeting-start-date" disabled={isLinkedMode}>
                <input
                  id="softhold-meeting-start-date"
                  type="date"
                  {...form.register("summitMeetingStart", {
                    onChange: (event) => {
                      const value = event.target.value;
                      const currentEnd = form.getValues("summitMeetingEnd");
                      if (!currentEnd || currentEnd < value) {
                        form.setValue("summitMeetingEnd", value);
                      }
                    }
                  })}
                  readOnly={isLinkedMode}
                  className={inputCls(false, true)}
                />
              </Field>

              <Field label="Summit Meeting End" htmlFor="softhold-meeting-end-date" error={form.formState.errors.summitMeetingEnd?.message} disabled={isLinkedMode}>
                <input
                  id="softhold-meeting-end-date"
                  type="date"
                  min={form.watch("summitMeetingStart") || undefined}
                  {...form.register("summitMeetingEnd")}
                  readOnly={isLinkedMode}
                  className={inputCls(!!form.formState.errors.summitMeetingEnd, true)}
                />
              </Field>

              <Field
                label="Meeting Name"
                htmlFor="softhold-meeting-name"
                required
                error={form.formState.errors.meetingName?.message}
                disabled={isLinkedMode}
              >
                <input
                  id="softhold-meeting-name"
                  type="text"
                  {...form.register("meetingName")}
                  readOnly={isDetailsMode}
                  className={inputCls(!!form.formState.errors.meetingName, true)}
                />
              </Field>

              <Field
                label="Meeting Type"
                htmlFor="softhold-meeting-type"
                required
                error={form.formState.errors.meetingType?.message}
                disabled={isLinkedMode}
              >
                <select
                  id="softhold-meeting-type"
                  {...form.register("meetingType", {
                    onChange: () => {
                      form.setValue("category", "");
                      form.setValue("subCategory", "");
                    }
                  })}
                  className={inputCls(!!form.formState.errors.meetingType, true)}
                  disabled={isMeetingTypesPending || isLinkedMode}
                >
                  <option value="">Select meeting type</option>
                  {meetingTypes.map((meetingType) => (
                    <option key={meetingType.id} value={meetingType.name}>
                      {meetingType.name}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Category" htmlFor="softhold-category" disabled={!selectedMeetingTypeId || isLinkedMode}>
                <select
                  id="softhold-category"
                  {...form.register("category", {
                    onChange: () => {
                      form.setValue("subCategory", "");
                    }
                  })}
                  className={inputCls(false, true)}
                  disabled={!selectedMeetingTypeId || isLinkedMode}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.category}>
                      {category.category}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Subcategory" htmlFor="softhold-sub-category" disabled={!selectedCategoryId || isLinkedMode}>
                <select
                  id="softhold-sub-category"
                  {...form.register("subCategory")}
                  className={inputCls(false, true)}
                  disabled={!selectedCategoryId || isLinkedMode}
                >
                  <option value="">Select subcategory</option>
                  {subCategories.map((subCategory) => (
                    <option key={subCategory.id} value={subCategory.subCategory}>
                      {subCategory.subCategory}
                    </option>
                  ))}
                </select>
              </Field>

              <input type="hidden" {...form.register("businessAdvisorEmail")} />
            </div>
          </section>

          <section className="space-y-3 border-t border-slate-200 pt-4">
            <h3 className="text-sm font-semibold text-slate-900">Dates & Room Blocks</h3>
            <h3 className="text-sm font-semibold text-slate-900">Dates (in order of preference)</h3>
            <div className="space-y-4">
              {roomBlockOptions.map((option) => {
                const optionErrors = roomBlockOptionErrors[option.id] ?? {};
                const isExpanded = expandedOptionId === option.id;

                return (
                  <div key={option.id} className="rounded-none border border-slate-200 p-3">
                    <div className="mb-2 flex items-center justify-between gap-2">
                      <div className="flex min-w-0 items-center gap-2 px-1 py-1 text-left">
                         <span>
                                            <svg width="6" height="14" viewBox="0 0 6 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M0 0H1.99219V1.9933H0V0ZM0 6.00335H1.99219V7.99665H0V6.00335ZM0 12.0067H1.99219V14H0V12.0067ZM4.00781 0H6V1.9933H4.00781V0ZM4.00781 6.00335H6V7.99665H4.00781V6.00335ZM4.00781 12.0067H6V14H4.00781V12.0067Z" fill="black" />
                                            </svg>
                                        </span>
                        <input
                          type="checkbox"
                          checked={option.isSelected}
                          readOnly
                          className="h-4 w-4 rounded-none border border-slate-400 accent-black cursor-not-allowed"
                        />
                        <span className="truncate text-sm text-slate-800">
                          {`${option.startDate || "--"} - ${option.endDate || "--"}`}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedOptionId((currentExpandedId) =>
                            currentExpandedId === option.id ? null : option.id
                          )
                        }
                        className="ml-auto rounded-none px-1 py-1 hover:bg-slate-50"
                        aria-label={isExpanded ? "Collapse option" : "Expand option"}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4 text-slate-600" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-slate-600" />
                        )}
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                      <Field label="Room Block Start Date" htmlFor={`softhold-start-date-${option.id}`} required error={optionErrors.startDate}>
                        <input
                          id={`softhold-start-date-${option.id}`}
                          type="date"
                          value={option.startDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => {
                              // Auto-set meeting start if not set or if it's before the new room block start
                              let nextMeetingStart = previousOption.meetingStartDate;
                              if (!nextMeetingStart || nextMeetingStart < value) {
                                nextMeetingStart = value;
                              }
                              
                              // Auto-set meeting end if not set
                              let nextMeetingEnd = previousOption.meetingEndDate;
                              if (!nextMeetingEnd) {
                                nextMeetingEnd = value;
                              }
                              
                              const nextEndDate =
                                !previousOption.endDate || previousOption.endDate < value
                                  ? value
                                  : previousOption.endDate;
                              return {
                                ...previousOption,
                                startDate: value,
                                endDate: nextEndDate,
                                meetingStartDate: nextMeetingStart,
                                meetingEndDate: nextMeetingEnd,
                                blockCountDayWise: rebuildBlockDays(
                                  value,
                                  nextEndDate,
                                  previousOption.blockCountDayWise
                                )
                              };
                            });
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(Boolean(optionErrors.startDate), true)}
                        />
                      </Field>

                      <Field label="Room Block End Date" htmlFor={`softhold-end-date-${option.id}`} required error={optionErrors.endDate}>
                        <input
                          id={`softhold-end-date-${option.id}`}
                          type="date"
                          min={option.startDate || undefined}
                          value={option.endDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => ({
                              ...previousOption,
                              endDate: value,
                              blockCountDayWise: rebuildBlockDays(
                                previousOption.startDate,
                                value,
                                previousOption.blockCountDayWise
                              )
                            }));
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(Boolean(optionErrors.endDate), true)}
                        />
                      </Field>

                      <Field label="Meeting Start Date" htmlFor={`softhold-room-meeting-start-date-${option.id}`}>
                        <input
                          id={`softhold-room-meeting-start-date-${option.id}`}
                          type="date"
                          value={option.meetingStartDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => ({
                              ...previousOption,
                              meetingStartDate: value,
                              meetingEndDate:
                                !previousOption.meetingEndDate || previousOption.meetingEndDate < value
                                  ? value
                                  : previousOption.meetingEndDate
                            }));
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(false, true)}
                        />
                      </Field>

                      <Field label="Meeting End Date" htmlFor={`softhold-room-meeting-end-date-${option.id}`} error={optionErrors.meetingEndDate}>
                        <input
                          id={`softhold-room-meeting-end-date-${option.id}`}
                          type="date"
                          min={option.meetingStartDate || undefined}
                          value={option.meetingEndDate}
                          onChange={(event) => {
                            const value = event.target.value;
                            updateRoomBlockOption(option.id, (previousOption) => ({
                              ...previousOption,
                              meetingEndDate: value
                            }));
                          }}
                          readOnly={isDetailsMode}
                          className={inputCls(Boolean(optionErrors.meetingEndDate), true)}
                        />
                      </Field>
                      <label className="mb-1 block text-xs font-semibold text-slate-700">Room Block Count by Day</label>

                     
                      
                       
                     

                      {option.blockCountDayWise.length > 0 && (
                      <div className="md:col-span-2">
                        <div className="grid grid-cols-12 items-center gap-2 pt-1 pb-1">
                         
                             <div className="col-span-7 text-sm text-slate-700">
                                  Total Attendees
                              </div>
                              <div className="col-span-5">
                                <input
                                    id={`softhold-estimated-attendance-${option.id}`}
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={option.estimatedAttendance}
                                    onChange={(event) => {
                                      const parsed = Number(event.target.value);
                                      const nextValue = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
                                      updateRoomBlockOption(option.id, (previousOption) => ({
                                        ...previousOption,
                                        estimatedAttendance: nextValue
                                      }));
                                    }}
                                    readOnly={isDetailsMode}
                                    className={inputCls(false)} style={{ width: "120px",float: "inline-end" }}
                                  />
                              </div>
                         
                        </div>
                            {option.blockCountDayWise.map((dayRow, dayIndex) => (
                              <div key={dayRow.day} className="grid grid-cols-12 items-center gap-2 pt-1 pb-1">
                                
                                <div className="col-span-7 text-sm text-slate-700">
                                  {format(parseLocalDate(dayRow.day), "MM-dd-yyyy")}
                                </div>
                                <div className="col-span-5">
                                  <input
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={dayRow.blockCount}
                                    onChange={(event) => {
                                      const parsed = Number(event.target.value);
                                      const nextCount = Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
                                      updateRoomBlockOption(option.id, (previousOption) => ({
                                        ...previousOption,
                                        blockCountDayWise: previousOption.blockCountDayWise.map((row, rowIndex) =>
                                          rowIndex === dayIndex ? { ...row, blockCount: nextCount } : row
                                        )
                                      }));
                                    }}
                                    readOnly={isDetailsMode}
                                    className={inputCls(false)} style={{ width: "120px",float: "inline-end" }}
                                  />
                                </div>
                              </div>
                            ))}
                          </div>
                        
                      )}

                      {!isDetailsMode && (
                        <div className="md:col-span-2 flex items-center justify-end gap-4">
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              role="switch"
                              aria-checked={option.isItPrimaryDate}
                              onClick={() =>
                                updateRoomBlockOption(option.id, (prev) => ({
                                  ...prev,
                                  isItPrimaryDate: !prev.isItPrimaryDate
                                }))
                              }
                              className={cn(
                                "relative inline-flex h-1 w-9 shrink-0 items-center transition-colors focus:outline-none",
                                option.isItPrimaryDate ? "bg-black" : "bg-[#9EA3A0]"
                              )}
                            >
                              <span
                                className={cn(
                                  "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white border-2 border-black transition-transform",
                                  option.isItPrimaryDate ? "translate-x-4" : "translate-x-0"
                                )}
                              />
                            </button>
                            <span className="text-sm text-slate-700 select-none">Selected</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeRoomBlockOption(option.id)}
                            disabled={roomBlockOptions.length <= 1}
                            className="rounded-none border border-[#BBBCBC] px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      </div>
                    )}
                  </div>
                );
              })}

              {!isDetailsMode && (
                <div className="flex justify-center">
                  <button
                    type="button"
                    onClick={addRoomBlockOption}
                    className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    Add Option
                  </button>
                </div>
              )}
            </div>
          </section>

          {!isDetailsMode && (
            <section className="space-y-2 border-t border-slate-200 pt-4">
              <Field label="Notes" htmlFor="softhold-notes">
                <textarea
                  id="softhold-notes"
                  rows={4}
                  {...form.register("notes")}
                  className={inputCls(false, true)}
                />
              </Field>
            </section>
          )}
{isDetailsMode && softHoldDetails && (
              <section className="mt-4 space-y-3 border-t border-slate-200 pt-4">
                <h3 className="text-sm font-semibold text-slate-900">Comments</h3>

                {canEditSoftHold && (
                  <div className="space-y-2">
                    <textarea
                      id="softhold-new-comment"
                      rows={4}
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment..."
                      className="w-full rounded-none border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-0"
                    />
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => void handleAddComment()}
                        disabled={!newComment.trim() || addSoftHoldComment.isPending}
                        className="inline-flex items-center gap-2 rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {addSoftHoldComment.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                        Add Comment
                      </button>
                    </div>
                  </div>
                )}

                {softHoldDetails.softholdComments.filter((c) => c.isActive).length > 0 && (
                  <ul className="divide-y divide-slate-200 border border-slate-200">
                    {[...softHoldDetails.softholdComments]
                      .filter((c) => c.isActive)
                      .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
                      .map((comment) => {
                        const displayName = comment.userName || comment.createdBy;
                        const initials = getSoftHoldInitials(displayName);
                        const isEditing = editingCommentId === comment.id;
                        const canEditThisComment =
                          canEditSoftHold &&
                          comment.createdBy.toLowerCase() === userEmail.toLowerCase();

                        return (
                          <li key={comment.id} className="px-3 py-3">
                            <div className="mb-1.5 flex items-center gap-2">
                              <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold text-slate-700">
                                {initials}
                              </span>
                              <span className="text-sm font-semibold text-slate-900">{displayName}</span>
                              <span className="text-sm text-slate-400">|</span>
                              <span className="text-xs text-slate-500">
                                {format(parseLocalDate(comment.createdDate), "MM/dd/yyyy")}
                              </span>
                            </div>

                            {isEditing ? (
                              <div className="ml-9 space-y-2">
                                <textarea
                                  rows={3}
                                  value={editingCommentText}
                                  onChange={(e) => setEditingCommentText(e.target.value)}
                                  className="w-full rounded-none border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-0"
                                />
                                <div className="flex gap-2">
                                  <button
                                    type="button"
                                    onClick={() => void handleSaveEditComment(comment.id)}
                                    disabled={!editingCommentText.trim() || editSoftHoldComment.isPending}
                                    className="inline-flex items-center gap-1 rounded-none border border-slate-900 bg-slate-900 px-3 py-1.5 text-xs text-white hover:bg-slate-800 disabled:opacity-50"
                                  >
                                    {editSoftHoldComment.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => { setEditingCommentId(null); setEditingCommentText(""); }}
                                    className="rounded-none border border-slate-300 px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="ml-9">
                                <p className="whitespace-pre-wrap text-sm text-slate-700">{comment.comment}</p>
                                {canEditThisComment && (
                                  <div className="mt-1 flex justify-end">
                                    <button
                                      type="button"
                                      onClick={() => { setEditingCommentId(comment.id); setEditingCommentText(comment.comment); }}
                                      className="text-xs text-slate-500 underline hover:text-slate-900"
                                    >
                                      Edit
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </li>
                        );
                      })}
                  </ul>
                )}
              </section>
            )}

            </form>
            </div>
          </div>
            

           
                   
        )}

        <div className="flex shrink-0 justify-end gap-2 px-4 py-3">
              {isDetailsMode && softHoldDetails && (
                <>
                  <button
                    type="button"
                    onClick={() => setIsEditingMode(true)}
                    disabled={!canEditSoftHold || softHoldDetails.isResolved}
                    title={
                      !canEditSoftHold
                        ? "You do not have permission to edit soft holds."
                        : softHoldDetails.isResolved
                          ? "Resolved soft holds cannot be edited."
                          : undefined
                    }
                    className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={openResolveDialog}
                    disabled={!canEditSoftHold || softHoldDetails.isResolved}
                    title={
                      !canEditSoftHold
                        ? "You do not have permission to resolve soft holds."
                        : softHoldDetails.isResolved
                          ? "This soft hold is already resolved."
                          : undefined
                    }
                    className="rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300"
                  >
                    Resolve
                  </button>
                </>
              )}
              <button
                type="button"
                onClick={() => {
                  if (isEditingMode && hasExistingSoftHold) {
                    setIsEditingMode(false);
                    return;
                  }
                  onClose();
                }}
                disabled={createSoftHold.isPending || editSoftHold.isPending}
                className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                {isDetailsMode ? "Close" : hasExistingSoftHold ? "Back" : "Cancel"}
              </button>
              {!isDetailsMode && (
                <button
                  type="submit"
                  form="create-soft-hold-form"
                  disabled={createSoftHold.isPending || editSoftHold.isPending || !!meetingLookupError}
                  className={cn(
                    "inline-flex items-center gap-2 rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white",
                    "hover:bg-slate-800 disabled:opacity-60"
                  )}
                >
                  {(createSoftHold.isPending || editSoftHold.isPending) && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {isEditingMode ? "Update" : "Save"}
                </button>
              )}
            </div>  
   
      </div>

      {showResolveDialog && softHoldDetails && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 px-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="resolve-soft-hold-title"
          onClick={() => setShowResolveDialog(false)}
        >
          <div
            className="w-full max-w-xl rounded-none border border-slate-200 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-slate-200 px-4 py-3">
              <h3 id="resolve-soft-hold-title" className="text-base font-semibold text-slate-900">
                Resolve Soft Hold
              </h3>
              <p className="mt-1 text-sm text-slate-600">
                Please select the reason for resolving this item.
              </p>
            </div>

            <div className="space-y-4 px-4 py-4">
              <div>
                <label htmlFor="resolve-reason" className="mb-1 block text-sm font-semibold text-slate-800">
                  Select Reason <span className="text-red-600">*</span>
                </label>
                <select
                  id="resolve-reason"
                  value={resolveStatus}
                  onChange={(e) => {
                    setResolveStatus(e.target.value);
                    setResolveStatusError(null);
                    setResolveDateError(null);
                    setResolvePreferredDateId("");
                  }}
                  className="w-full rounded-none border border-slate-300 px-3 py-2 text-sm"
                >
                  <option value="">--</option>
                  {resolveReasons.map((reason) => (
                    <option key={reason} value={reason}>
                      {reason}
                    </option>
                  ))}
                </select>
                {resolveStatusError && <p className="mt-1 text-xs text-red-600">{resolveStatusError}</p>}
              </div>

              {resolveStatus === "Confirmed Event" && (
                <>
                  <div>
                    <label htmlFor="resolve-confirmed-date" className="mb-1 block text-sm font-semibold text-slate-800">
                      Select Confirmed Date(s) <span className="text-red-600">*</span>
                    </label>
                    <select
                      id="resolve-confirmed-date"
                      value={resolvePreferredDateId}
                      onChange={(e) => {
                        const value = e.target.value ? Number(e.target.value) : "";
                        setResolvePreferredDateId(value);
                        setResolveDateError(null);
                      }}
                      className="w-full rounded-none border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="">--</option>
                      {softHoldDetails.preferredDates.map((date) => (
                        <option key={date.id} value={date.id}>
                          {toDateInputValue(date.startDate)} - {toDateInputValue(date.endDate)}
                        </option>
                      ))}
                    </select>
                    {resolveDateError && <p className="mt-1 text-xs text-red-600">{resolveDateError}</p>}
                  </div>

                  <div>
                    <label htmlFor="resolve-meeting-id" className="mb-1 block text-sm font-semibold text-slate-800">Meeting ID</label>
                    <input
                      id="resolve-meeting-id"
                      type="text"
                      value={softHoldDetails.meetingId || "---"}
                      readOnly
                      className="w-full rounded-none border border-slate-300 bg-slate-100 px-3 py-2 text-sm text-slate-600"
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="resolve-notes" className="mb-1 block text-sm font-semibold text-slate-800">Notes</label>
                <textarea
                  id="resolve-notes"
                  value={resolveNotes}
                  onChange={(e) => setResolveNotes(e.target.value)}
                  rows={4}
                  className="w-full rounded-none border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-slate-200 px-4 py-3">
              <button
                type="button"
                onClick={() => setShowResolveDialog(false)}
                disabled={resolveSoftHold.isPending}
                className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleResolveSave()}
                disabled={resolveSoftHold.isPending}
                className="inline-flex items-center gap-2 rounded-none border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white hover:bg-slate-800 disabled:opacity-60"
              >
                {resolveSoftHold.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function toDateInputValue(value?: string | null) {
  if (!value) return "";
  // Extract YYYY-MM-DD directly from the string without constructing a Date
  // object, so no UTC↔local conversion can shift the date.
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
  return match ? match[1] : "";
}

/**
 * Parse a date string (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss) as a LOCAL midnight
 * Date, so date-fns `format` never shifts the date by timezone.
 */
function parseLocalDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return new Date(Number.NaN);
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function buildBlockCountDays(startDate: string, endDate: string) {
  const dates: Array<{ day: string; blockCount: number; isDeleted: boolean }> = [];
  if (!startDate || !endDate || endDate < startDate) return dates;

  // Parse as local date by splitting the string to avoid timezone offset
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  
  const cursor = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);

  while (cursor <= end) {
    // Format as ISO string (YYYY-MM-DDTHH:mm:ss) in local timezone
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    const day = String(cursor.getDate()).padStart(2, '0');
    
    dates.push({
      day: `${year}-${month}-${day}T00:00:00`,
      blockCount: 0,
      isDeleted: false
    });
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function buildDayRange(startDate: string, endDate: string) {
  const dates: string[] = [];
  if (!startDate || !endDate || endDate < startDate) return dates;

  // Parse as local date by splitting the string to avoid timezone offset
  const [startYear, startMonth, startDay] = startDate.split('-').map(Number);
  const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
  
  const cursor = new Date(startYear, startMonth - 1, startDay);
  const end = new Date(endYear, endMonth - 1, endDay);

  while (cursor <= end) {
    // Format as YYYY-MM-DD in local timezone
    const year = cursor.getFullYear();
    const month = String(cursor.getMonth() + 1).padStart(2, '0');
    const day = String(cursor.getDate()).padStart(2, '0');
    
    dates.push(`${year}-${month}-${day}`);
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
}

function Field({
  label,
  htmlFor,
  required,
  error,
  disabled,
  children
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  children: import("react").ReactNode;
}) {
  return (
    <div>
      <div
        className={cn(
          "border border-[#D0D0CE] px-1.5 pb-1.5",
          disabled && "bg-black/[0.04]"
        )}
      >
        <label
          htmlFor={htmlFor}
          className={cn(
            "mt-0.5 ml-1.5 block text-[11px] font-semibold",
            disabled ? "text-black" : "text-[#53565A]"
          )}
        >
          {label}
          {required ? <span className="text-red-600 ml-0.5">*</span> : null}
        </label>
        {children}
      </div>
      {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
    </div>
  );
}

function inputCls(hasError: boolean, inField = false) {
  return cn(
    "w-full rounded-none text-sm focus:outline-none focus:ring-0",
    inField
      ? "border-0 bg-transparent px-1.5 py-1.5"
      : "border px-3 py-2 border-slate-300 focus:border-slate-500",
    hasError && (inField ? "text-red-600" : "border-red-500")
  );
}
